import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Bell, Lock, Users, Upload, Download, FileText, Tag, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GlassCard, GradButton, Field } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import api, { errMsg } from "@/lib/api";

const NotForm = ({ tur, kayitId, onClose, onDone }) => {
  const [f, setF] = useState({ baslik: "", metin: "", hatirlatma: "", grup: "ozel" });
  const [hat, setHat] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const kaydet = async () => {
    try { await api.post("/kayit-notlari", { ...f, tur, kayit_id: kayitId, hatirlatma: hat ? f.hatirlatma || null : null }); toast.success("Not eklendi"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}><DialogContent className="glass max-w-lg" data-testid="not-form">
      <DialogHeader><DialogTitle>Not Ekle</DialogTitle></DialogHeader>
      <Field label="Başlık *"><Input data-testid="not-baslik" value={f.baslik} onChange={(e) => set("baslik")(e.target.value)} className="bg-white/5" /></Field>
      <Field label="Not"><Textarea data-testid="not-metin" rows={5} value={f.metin} onChange={(e) => set("metin")(e.target.value)} className="bg-white/5" /></Field>
      <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="not-hatirlatma-ac" checked={hat} onChange={(e) => setHat(e.target.checked)} />Hatırlatma</label>
      {hat && <Input data-testid="not-hatirlatma" type="date" value={f.hatirlatma} onChange={(e) => set("hatirlatma")(e.target.value)} className="bg-white/5" />}
      <div className="grid grid-cols-2 gap-3">{[["ozel", "Özel not", "Sadece siz görürsünüz", Lock], ["sirket", "Şirket notu", "Herkes görür", Users]].map(([k, l, a, I]) => (
        <button key={k} data-testid={`not-grup-${k}`} onClick={() => set("grup")(k)} className={`text-left p-3 rounded-xl border transition-colors ${f.grup === k ? "border-[var(--g1)] bg-white/5" : "border-white/10 hover:bg-white/5"}`}>
          <div className="flex items-center gap-2 text-sm font-semibold"><I className="w-4 h-4" />{l}</div><div className="text-[11px] text-muted-foreground mt-1">{a}</div></button>))}</div>
      <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 h-10 rounded-full text-sm bg-white/5">Kapat</button><GradButton data-testid="not-kaydet" onClick={kaydet} disabled={!f.baslik.trim()}>Kaydet</GradButton></div>
    </DialogContent></Dialog>
  );
};

export const KayitNotlari = ({ tur, kayitId }) => {
  const [l, setL] = useState([]);
  const [form, setForm] = useState(false);
  const yukle = () => api.get("/kayit-notlari", { params: { tur, kayit_id: kayitId } }).then((r) => setL(r.data));
  useEffect(() => { yukle(); }, [kayitId]); // eslint-disable-line react-hooks/exhaustive-deps
  const sil = async (id) => { try { await api.delete(`/kayit-notlari/${id}`); yukle(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kayit-notlari">
      <button data-testid="not-ekle" onClick={() => setForm(true)} className="min-h-[140px] rounded-2xl border-2 border-dashed border-white/15 hover:bg-white/5 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground transition-colors"><Plus className="w-6 h-6" />Not ekle</button>
      {l.map((n) => (
        <GlassCard key={n.id} className="p-4" data-testid={`not-${n.id}`}>
          <div className="flex items-start gap-2"><div className="font-semibold text-sm flex-1">{n.baslik}</div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10">{n.grup === "sirket" ? "Şirket" : "Özel"}</span>
            <button data-testid={`not-sil-${n.id}`} onClick={() => sil(n.id)} className="p-1 rounded hover:bg-white/10"><Trash2 className="w-3.5 h-3.5" /></button></div>
          <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{n.metin}</p>
          <div className="text-[11px] text-muted-foreground mt-3 flex flex-wrap gap-2">{n.yazan_ad} · {n.created_at.slice(0, 10)}{n.hatirlatma && <span className="text-amber-300 inline-flex items-center gap-1"><Bell className="w-3 h-3" />{n.hatirlatma}</span>}</div>
        </GlassCard>))}
      {form && <NotForm tur={tur} kayitId={kayitId} onClose={() => setForm(false)} onDone={() => { setForm(false); yukle(); }} />}
    </div>
  );
};

const boyut = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

export const KayitDosyalari = ({ tur, kayitId }) => {
  const [l, setL] = useState([]);
  const [busy, setBusy] = useState(false);
  const yukle = () => api.get("/kayit-dosyalari", { params: { tur, kayit_id: kayitId } }).then((r) => setL(r.data));
  useEffect(() => { yukle(); }, [kayitId]); // eslint-disable-line react-hooks/exhaustive-deps
  const ekle = async (files) => {
    setBusy(true);
    for (const f of files) {
      if (f.size > 5 * 1048576) { toast.error(`${f.name}: en fazla 5 MB`); continue; }
      const fd = new FormData(); fd.append("tur", tur); fd.append("kayit_id", kayitId); fd.append("dosya", f);
      try { await api.post("/kayit-dosyalari", fd); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false); yukle();
  };
  const indir = async (d) => {
    const r = await api.get(`/kayit-dosyalari/${d.id}/indir`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = d.ad; a.click(); URL.revokeObjectURL(a.href);
  };
  const sil = async (id) => { try { await api.delete(`/kayit-dosyalari/${id}`); yukle(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4" data-testid="kayit-dosyalari">
      <label data-testid="dosya-ekle" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); ekle([...e.dataTransfer.files]); }}
        className="flex flex-col items-center justify-center gap-2 min-h-[150px] rounded-2xl border-2 border-dashed border-white/15 hover:bg-white/5 cursor-pointer text-sm text-muted-foreground transition-colors">
        <Upload className="w-6 h-6" />{busy ? "Yükleniyor…" : "Dosya Ekle — sürükleyin veya tıklayın"}<span className="text-[11px]">jpg, png, bmp, gif, doc, docx, xlsx, csv, pdf · en fazla 5 MB</span>
        <input type="file" multiple className="hidden" data-testid="dosya-input" accept=".jpg,.jpeg,.png,.bmp,.gif,.doc,.docx,.xlsx,.xls,.csv,.pdf" onChange={(e) => { ekle([...e.target.files]); e.target.value = ""; }} />
      </label>
      {l.map((d) => (
        <GlassCard key={d.id} className="p-3 flex items-center gap-3" data-testid={`dosya-${d.id}`}>
          <FileText className="w-5 h-5 text-[var(--g1)]" /><div className="min-w-0 flex-1"><div className="text-sm truncate">{d.ad}</div><div className="text-[11px] text-muted-foreground">{boyut(d.boyut)} · {d.yukleyen} · {d.created_at.slice(0, 10)}</div></div>
          <button data-testid={`dosya-indir-${d.id}`} onClick={() => indir(d)} className="p-2 rounded hover:bg-white/10"><Download className="w-4 h-4" /></button>
          <button data-testid={`dosya-sil-${d.id}`} onClick={() => sil(d.id)} className="p-2 rounded hover:bg-white/10"><Trash2 className="w-4 h-4" /></button>
        </GlassCard>))}
    </div>
  );
};

export const EtiketDuzenle = ({ deger, onKaydet }) => {
  const [v, setV] = useState(deger || "");
  const [acik, setAcik] = useState(false);
  useEffect(() => { setV(deger || ""); }, [deger]);
  return (
    <Popover open={acik} onOpenChange={setAcik}>
      <PopoverTrigger asChild><button data-testid="etiket-duzenle" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-white/10 hover:bg-white/15"><Tag className="w-3 h-3" />{deger || "Etiketi Düzenle"}</button></PopoverTrigger>
      <PopoverContent className="glass w-72 space-y-2" align="start">
        <Input data-testid="etiket-input" placeholder="Virgülle ayırın" value={v} onChange={(e) => setV(e.target.value)} className="bg-white/5" />
        <GradButton data-testid="etiket-kaydet" onClick={async () => { await onKaydet(v.trim()); setAcik(false); }}>Kaydet</GradButton>
      </PopoverContent>
    </Popover>
  );
};

export const excelAktar = (ad, kolonlar, satirlar) => {
  const h = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
  const csv = [kolonlar.map((k) => h(k[0])).join(";"), ...satirlar.map((r) => kolonlar.map((k) => h(typeof k[1] === "function" ? k[1](r) : r[k[1]])).join(";"))].join("\r\n");
  const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  a.download = `${ad}_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

export const useSayfa = (satirlar, bagimlilik) => {
  const [adet, setAdet] = useState(25);
  const [s, setS] = useState(1);
  useEffect(() => { setS(1); }, [bagimlilik, adet]);
  const son = Math.max(1, Math.ceil(satirlar.length / adet));
  const sayfa = Math.min(s, son);
  const bas = (sayfa - 1) * adet;
  const Sayfalama = () => (
    <div className="flex flex-wrap items-center justify-end gap-3 mt-3 text-xs text-muted-foreground" data-testid="sayfalama">
      <div className="w-20"><SelectBox testid="sayfa-adet" value={String(adet)} onChange={(v) => setAdet(+v)} options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))} /></div>
      <span data-testid="sayfa-bilgi">{satirlar.length ? `${bas + 1}-${Math.min(bas + adet, satirlar.length)}` : 0} / Toplam {satirlar.length} kayıt</span>
      <button data-testid="sayfa-onceki" disabled={sayfa <= 1} onClick={() => setS(sayfa - 1)} className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
      <span>{sayfa}/{son}</span>
      <button data-testid="sayfa-sonraki" disabled={sayfa >= son} onClick={() => setS(sayfa + 1)} className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
  return { gorunen: satirlar.slice(bas, bas + adet), Sayfalama };
};

export const useSecim = () => {
  const [sec, setSec] = useState(new Set());
  const degis = (id) => setSec((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const hepsi = (ids, v) => setSec(v ? new Set(ids) : new Set());
  return { sec, degis, hepsi, temizle: () => setSec(new Set()) };
};
